import {
  Buyer,
  CartHoldBlockers,
  DeliveryType,
  GiftCard,
  IntegrationGroupType,
  OrderStatus,
  ProductDisplayStep,
  ProductFrequentValueModel,
} from '@digilize/shared/definitions/src';

export interface PriceResponse {
  id: string;
  tax_id: string;
  name: string;
  available_from: string;
  regular_amount: number;
  amount: number;
  total_amount: number;
  tax_rate: number;
}

export type ProductAvailabilityKind = 'stock' | 'seats' | 'infinite';

export interface ProductAvailabilityDto {
  kind: ProductAvailabilityKind;
  available: number | null;
  total: number | null;
  sold_out: boolean;
  almost_sold_out: boolean;
  show_id: string | null;
}

/**
 * swagger: ProductsGroupModel — an active product group a product belongs to. `order` is the
 * group's own position among groups, NOT the product's position within it (that is the product's
 * own `order`). Only the fields the shop renders are declared.
 */
export interface ProductGroupDto {
  id: string;
  name: string | null;
  order: number;
}

export interface ProductResponse {
  id: string;
  company_id: string;
  public_name: string;
  subtitle: string;
  description: string | null;
  url: string | null;
  sku: string | null;
  barcode: string | null;
  maker: string | null;
  tags: string;
  label: string;
  type: number;
  available_from: string;
  available_to: string;
  highlighted_to: string | null;
  highlighted_to_error: boolean;
  instances_infinite: boolean;
  instances_allow_exceed_amount: boolean;
  instances_available: number;
  instances_amount: number;
  instances: number;
  max_quantity_per_order: number | null;
  instances_amount_difference: number;
  general_ledger_account_id: string | null;
  current_price: PriceResponse;
  color: string | null;
  media_ids: string[];
  media: MediaDto[] | null;
  prices: string | null;
  shop_ids: string | null;
  shops: string | null;
  seat_type_ids: string[] | null;
  variants: string | null;
  group_ids: string[];
  /** Active groups only, sorted by the group's own order then name — `groups[0]` is the primary one. */
  groups: ProductGroupDto[] | null;
  supplement_ids: string[];
  supplement_products: string | null;
  descriptors: string[];
  voucher_description: string | null;
  voucher_available_to: string | null;
  voucher_generate_codes: boolean;
  voucher_single_code: string | null;
  voucher_discount: string | null;
  voucher_photo_id: string | null;
  voucher_photo: string | null;
  import_feed_entry_id: string | null;
  upsell_ids: string[];
  order: number;
  has_reservation: boolean;
  required_payment_method_id: string | null;
  is_cineville_ticket: boolean;
  is_subscription_ticket: boolean;
  availability: ProductAvailabilityDto | null;
  /** The amounts below are only sold when this is on; off, the product is sold at `current_price`. */
  show_most_frequent_values: boolean;
  /** Sorted by `order` server-side, so array order is the order to render them in (PAUL-3071). */
  most_frequent_values: ProductFrequentValueModel[] | null;
}

export interface SupplementResponse {
  supplement_id: string;
  is_required: boolean;
  is_limited_to_main_product_quantity: boolean;
  is_mutually_exclusive: boolean;
  main_product_id: string | null;
  display_step: ProductDisplayStep;
  id: string;
  company_id: string;
  public_name: string;
  subtitle: string;
  description: string | null;
  url: string | null;
  sku: string | null;
  barcode: string | null;
  maker: string | null;
  tags: string;
  label: string;
  type: number;
  available_from: string;
  available_to: string;
  highlighted_to: string | null;
  highlighted_to_error: boolean;
  instances_infinite: boolean;
  instances_allow_exceed_amount: boolean;
  instances_available: number;
  instances_amount: number;
  instances: number;
  max_quantity_per_order: number | null;
  instances_amount_difference: number;
  general_ledger_account_id: string | null;
  current_price: PriceResponse;
  color: string | null;
  media_ids: string[];
  media: MediaDto[] | null;
  prices: string | null;
  shop_ids: string | null;
  shops: string | null;
  variants: string | null;
  group_ids: string[];
  groups: string[];
  supplement_ids: string[];
  supplement_products: string[];
  descriptors: string[];
  voucher_description: string | null;
  voucher_available_to: string | null;
  voucher_generate_codes: boolean;
  voucher_single_code: string | null;
  voucher_discount: string | null;
  voucher_photo_id: string | null;
  voucher_photo: string | null;
  import_feed_entry_id: string | null;
  upsell_ids: string[];
  order: number | null;
  has_reservation: boolean;
  required_payment_method_id: string | null;
  is_cineville_ticket: boolean;
  is_subscription_ticket: boolean;
}

export interface MediaDto {
  id: string;
  name: string;
  name_without_extension: string;
  order: number;
  url1x: string;
  width: number;
  height: number;
  title: string | null;
  alt: string | null;
  hash: string;
  extension: string;
  is_extension_image: boolean;
  is_extension_vector: boolean;
}

export interface CartSummaryProductDto {
  product_id: string;
  title: string;
  type: number;
  amount: number;
  total: number;
  quantity: number;

  requires_account: boolean | null;
}

export interface CartSummaryLineDto {
  id: string;
  product_id: string | null;
  seat_id: string | null;
  movie_show_id: string | null;
  product_has_reservation: boolean;
  parent_cart_line_id: string | null;
  discount_code_id: string | null;
  cineville_barcode: string | null;
  /**
   * Subscription card redeemed on this line (PAUL-2844). Null while the line still has none — a
   * flagged ticket in that state is what `POST /orders` refuses, so it is also the checkout gate.
   */
  subscription_card_code?: string | null;
  subscription_card_id?: string | null;
  /**
   * What this line's term costs now: pro-rated from its start date, and NOT the recurring price.
   * Neither the setup fee nor the discount is inside it — each of those is a child line of its own
   * (see {@link subscription_line_role}), so what a subscription actually costs on the first invoice
   * is this line plus its children.
   */
  amount: number;
  /**
   * The operator's own wording for this line — the product's public name on a subscription line, the
   * configured setup-fee label ("Eenmalige aansluitingskosten") on a fee line. Null on a discount
   * line, which the shop names itself.
   */
  title?: string | null;
  /** What this line is within a subscription purchase; absent on a non-subscription line. */
  subscription_line_role?: SubscriptionLineRole;
  /**
   * First day of the billing period this line charges for (`2026-09-16T00:00:00+00:00`). Null on a
   * setup fee (a one-off charges for no period), on every non-subscription line, and while no start
   * date has been settled — a mandatory start date is priced provisionally off today, and the API
   * declines to name a period nobody agreed to (PAUL-3292).
   */
  period_start?: string | null;
  /**
   * **Exclusive** end of that period: the first instant NOT covered, matching the convention the
   * subscription invoices use. A period covering the second half of September comes back as
   * `2026-10-01`, so the last day it pays for is one day earlier — see `lastCoveredDay`. Printing it
   * raw names a day the buyer is not paying for.
   */
  period_end?: string | null;
  /** Present on subscription lines only (swagger: CartLineSubscriberModel). */
  subscriber?: CartLineSubscriberDto | null;
}

/**
 * swagger: SubscriptionLineRole — what a line contributes to a subscription purchase. The period
 * charge is the parent line; the fee and the discount are children of it, each carrying their own
 * amount so the invoice can break them out (PAUL-3184).
 */
export enum SubscriptionLineRole {
  /** The subscription itself: the pro-rated first period, which is what `period_start`/`period_end` name. */
  Subscription = 0,
  /** The one-off setup fee. Never pro-rated, never discounted, and bound to no period. */
  SetupFee = 1,
  /** A Discount to Subscription, as a negative amount over its own window — which can end before the period does. */
  Discount = 2,
}

/** swagger: CartLineSubscriberModel — subscriber data attached to a subscription cart line. */
export interface CartLineSubscriberDto {
  billing_period_id: string | null;
  start_date: string | null;
  /**
   * The first day this line may start, resolved against the billing period the line itself carries
   * (PAUL-3163) — the same date the purchase gate enforces. Null means the product has no floor;
   * absent on an order read, where the date is already settled.
   */
  first_allowed_start_date?: string | null;
  /**
   * One-off setup fee. Superseded by the fee's own line ({@link SubscriptionLineRole.SetupFee}), which
   * is where the amount is read from now; this stays null on every cart read path and is kept only for
   * an order placed before the fee got a line of its own (PAUL-3292).
   */
  setup_fee_amount: number | null;
  /** This line's billing-period price normalized to one month — drives the "€X per maand" summary row. */
  recurring_monthly_amount: number | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  birthday: string | null;
  gender: string | null;
  photo_id: string | null;
  photo: MediaDto | null;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  country: string | null;
}

/**
 * swagger: response of `PUT /v1.0/reservations/{cartLineId}/subscription-card` — the resulting
 * state of the line, both fields null when the card was detached.
 */
export interface SubscriptionCardAttachDto {
  subscription_card_code: string | null;
  subscription_card_id: string | null;
}

export interface ReservedSeatDto {
  seat_id: string;
  row: number;
  row_label: string;
  col: number;
  column_label: string;
  group: string;
}

export enum CartStatus {
  Expired = 'expired',
  Active = 'active',
  Confirmed = 'confirmed',
  Canceled = 'canceled',
  Ordered = 'ordered',
  Paid = 'paid',
}

export interface CartDiscountCode {
  discount_code_id: string;
  code: string | null;
  name: string | null;
  amount: number;
}

export interface Bioscoopcard {
  id: string;
  code: string;
  applied_amount: number;
  can_delete?: boolean;
}

export interface CartDto {
  server_time: string;
  id: string;
  reserved_until: string | null;
  pos_id: string | null;
  delivery_methods: DeliveryType[];
  shop_id: string;
  status: CartStatus;
  buyer: Buyer;
  delivery_method: DeliveryType | null;
  payment_method_external_id: string;
  reservation_code: string | null;
  on_hold: string | null;
  has_restaurant_reservation: boolean;
  hold_blockers?: CartHoldBlockers;
  summary: {
    total_payment_method_cost: number;
    total_amount: number;
    pending_amount: number;
    /** Sum of the subscription lines' recurring per-month prices — the "Totaal €X per maand" figure. */
    total_recurring_monthly: number;
    products: CartSummaryProductDto[];
    lines: CartSummaryLineDto[];
    reserved_seat_products: ReservedSeatDto[];
    gift_cards: GiftCard[];
    discounts: CartDiscountCode[];
    orders: CartSummaryOrderDto[];
    integration_reservations: IntegrationReservation[];
  };
}

export interface CartSummaryOrderDto {
  id: string;
  inserted: string; //date in ISO format
  status: OrderStatus;
}

export interface ProductReservationResponse {
  cart_id: string;
  cart_line_id: string;
  server_time: string;
  reserved_until: string | null;
  parent_cart_line_id: string | null;
  cart: CartDto;
}

export interface IntegrationReservation {
  type: IntegrationGroupType;
  restaurant: RestaurantReservation;
}

export interface RestaurantReservation {
  id: string;
  status: 'processing' | string;
  before: string; // ISO date format
  after: string; // ISO date format
  seats: number;
  products: RestaurantProductInfo[];
  email: string;
  full_name: string;
  phone: string;
  language: string;
  notes: string;
  table_start: string; // ISO date format, if not filled there was no reservation made
}

export interface RestaurantProductInfo {
  id: string;
  name: string;
}
